import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartTypeRegistry,
} from 'chart.js';
import { useMemo, memo, useRef } from 'react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

declare module 'chart.js' {
  interface PluginOptionsByType<TType extends keyof ChartTypeRegistry> {
    centerText?: {
      text?: string;
      subText?: string;
    };
  }
}

export interface CompetencyData {
  competency: string;
  rating: number;
  hoverMessages?: string[];
}

interface StatusData {
  label: string;
  value: number;
  color: string;
}

interface ChartProps {
  type: 'bar' | 'doughnut';
  data: CompetencyData[] | StatusData[];
  title?: string;
  showLegend?: boolean;
  showAllPercentages?: boolean;
  customColors?: string[];
  height?: number;
  maxValue?: number;
  isLevelLabel?: boolean;
  totalObjectives?: number;
  centerText?: string;
  stepSize?: number;
  isDecimal?: boolean;
  decimalPlaces?: number;
}

const defaultBarColors = [
  '#C084FC',
  '#235FE3',
  '#EF8C3A',
  '#20BC5B',
  '#9F20BC',
  '#4802EB',
];

const centerTextPlugin = {
  id: 'centerText',
  afterDraw: (chart: ChartJS) => {
    const { ctx, chartArea } = chart;
    const meta = chart.getDatasetMeta(0);
    const hasData = meta.data.length > 0;

    if (!hasData) return;

    const text = chart.options.plugins?.centerText?.text || '';
    const subText = chart.options.plugins?.centerText?.subText || '';

    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    // Draw main text
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '20px Arial';
    ctx.fillStyle = '#000000';
    ctx.fillText(text, centerX, centerY - 10);

    // Draw subtext
    ctx.font = '14px Arial';
    ctx.fillStyle = '#666666';
    ctx.fillText(subText, centerX, centerY + 10);
    ctx.restore();
  },
};

const Chart = ({
  type = 'bar',
  data,
  title = '',
  showLegend = true,
  showAllPercentages = false,
  customColors,
  height = 300,
  maxValue = 10,
  isLevelLabel,
  totalObjectives,
  centerText,
  stepSize = 1,
  isDecimal = false,
  decimalPlaces = 1,
}: ChartProps) => {
  const ref = useRef<ChartJS>(null);

  const chartData = useMemo(
    () => ({
      labels:
        type === 'bar'
          ? (data as CompetencyData[]).map((item) => item.competency)
          : (data as StatusData[]).map((item) => item.label),
      datasets: [
        {
          label: type === 'bar' ? 'Competency Ratings' : undefined,
          data:
            type === 'bar'
              ? (data as CompetencyData[]).map((item) => item.rating)
              : (data as StatusData[]).map((item) => item.value),
          backgroundColor:
            type === 'bar'
              ? customColors || defaultBarColors
              : (data as StatusData[]).map((item) => item.color),
          borderWidth: type === 'doughnut' ? 0 : 1,
        },
      ],
    }),
    [data, type, customColors]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          titleColor: '#000',
          bodyColor: '#666',
          borderColor: '#ddd',
          borderWidth: 1,
          padding: 10,
          displayColors: false,
          callbacks: {
            label: (context: any) => {
              const index = context.dataIndex;
              const item = data[index];
              if ('hoverMessages' in item && item.hoverMessages) {
                return item.hoverMessages;
              }
              return `${context.dataset.label || ''}: ${context.raw}`;
            },
          },
        },
        legend: {
          display: showLegend,
          position: 'right' as const,
          labels: {
            padding: 15,
            usePointStyle: type === 'doughnut',
            generateLabels: (chart: any) => {
              const datasets = chart.data.datasets[0];
              return chart.data.labels.map((label: string, index: number) => ({
                text:
                  type === 'bar'
                    ? `${label} (${datasets.data[index]}/${maxValue})`
                    : `${label} (${datasets.data[index]})`,
                fillStyle: datasets.backgroundColor[index],
                hidden: false,
                lineCap: undefined,
                lineDash: undefined,
                lineDashOffset: undefined,
                lineJoin: undefined,
                lineWidth: undefined,
                strokeStyle: undefined,
                pointStyle: type === 'doughnut' ? 'circle' : undefined,
              }));
            },
          },
        },
        title: {
          display: !!title,
          text: title,
          align: 'start' as const,
          margin: {
            left: 16,
          },
          font: {
            size: 16,
            weight: 'bold' as const,
          },
          padding: {
            bottom: 20,
          },
        },
        centerText: {
          text: totalObjectives?.toString() || '0',
          subText: centerText || '',
        },
      },
      scales:
        type === 'bar'
          ? {
              y: {
                beginAtZero: true,
                max: maxValue,
                ticks: {
                  stepSize: stepSize,
                  callback: (tickValue: string | number) => {
                    if (isLevelLabel) {
                      const level = Number(tickValue);
                      if (level > 0 && level < 1) {
                        return `Level 1 (-)`;
                      } else if (level % 1 !== 0) {
                        return `Level ${Math.floor(level)} (+)`;
                      }
                      return `Level ${level}     `;
                    }

                    // if is decimal, then round to 1 decimal place
                    if (Number.isInteger(Number(tickValue)) && isDecimal) {
                      return Number(tickValue).toFixed(decimalPlaces);
                    }
                    return tickValue;
                  },
                },
                grid: {
                  display: true,
                  drawBorder: false,
                  drawTicks: false,
                  drawOnChartArea: true,
                },
                border: {
                  display: false,
                  dash: [5, 5],
                },
              },
              x: {
                grid: {
                  display: false,
                  drawBorder: false,
                },
              },
            }
          : undefined,
    }),
    [
      showLegend,
      title,
      type,
      maxValue,
      isLevelLabel,
      stepSize,
      data,
      totalObjectives,
      centerText,
    ]
  );

  const ChartComponent = type === 'bar' ? Bar : Doughnut;

  return (
    <div style={{ height: height }} className="flex gap-8">
      <div className="flex-1">
        <ChartComponent
          data={chartData}
          options={options}
          plugins={type === 'doughnut' ? [centerTextPlugin] : []}
          // @ts-ignore
          ref={ref}
        />
      </div>

      {showAllPercentages && (
        <div className="flex flex-col gap-3 pt-8 min-w-[180px]">
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{
                  backgroundColor:
                    type === 'bar'
                      ? (customColors || defaultBarColors)[
                          index % defaultBarColors.length
                        ]
                      : (item as StatusData).color,
                }}
              />
              <span className="text-sm flex-1">
                {type === 'bar'
                  ? (item as CompetencyData).competency
                  : (item as StatusData).label}
              </span>
              <span className="text-sm font-medium">
                {type === 'bar'
                  ? `${(((item as CompetencyData).rating / maxValue) * 100).toFixed(0)}%`
                  : `${(item as StatusData).value}%`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(Chart);
